import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { Mail, Send, Bell, RefreshCw, Sparkles } from "lucide-react";

// Phase 36.16 — Notifications tab.
// Lets workspace owners preview / opt out of the Monday Activation Pulse,
// and (for legacy parity) toggle the Daily Morning Briefing + DARA Replies.
export default function NotificationsTab() {
  const [ws, setWs] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [sending, setSending] = useState(false);
  const [savingFlag, setSavingFlag] = useState(false);

  const reload = async () => {
    try {
      const [w, p] = await Promise.all([
        api.get("/workspace"),
        api.get("/admin/activation-pulse/preview"),
      ]);
      setWs(w.data);
      setPulse(p.data);
    } catch (e) {
      // Non-blocking — preview can fail if the user is too new
      try {
        const w = await api.get("/workspace");
        setWs(w.data);
      } catch (_) { /* noop */ }
    }
  };

  useEffect(() => { reload(); }, []);

  const sendMine = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/activation-pulse/send-mine");
      if (r.data.ok) {
        toast.success(`Sent to ${r.data.sent_to}`, { duration: 6000 });
      } else {
        const reasons = {
          opted_out: "You've opted out — toggle the switch back on first.",
          too_new: "Workspace is less than 24h old — try again tomorrow.",
          trial_expired: "Trial expired — please upgrade first.",
          super_admin: "Super-admin gets the cross-workspace Sales Pulse instead.",
          no_owner_email: "No owner email on file.",
        };
        toast.error(reasons[r.data.reason] || `Skipped: ${r.data.reason}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const toggle = async (field, value) => {
    setSavingFlag(true);
    try {
      await api.patch("/workspace", { [field]: value });
      setWs(prev => ({ ...prev, [field]: value }));
      toast.success(`${field.replace(/_/g, " ")} → ${value ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingFlag(false);
    }
  };

  const activationOn = ws?.activation_pulse_enabled !== false;  // default true
  const briefingOn = ws?.daily_briefing_enabled !== false;
  const repliesOn = ws?.email_replies_enabled !== false;

  return (
    <div className="space-y-5" data-testid="notifications-tab">
      {/* Activation Pulse */}
      <Panel title="// WEEKLY ACTIVATION PULSE · MONDAY 09:30 UTC" testId="activation-pulse-card">
        <div className="p-5 space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Every Monday morning we send you a one-screen Bloomberg-styled email summarising your week
            in DARA — insights actioned, hedges accepted, forecasts run, your streak — plus a personalised
            "next best action" tied to whatever you haven't done yet. Habit-loop companion to the existing Daily Morning Briefing.
          </p>
          {pulse && (
            <div className="border border-zinc-900 bg-zinc-950/40 p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// NEXT MONDAY'S PULSE WILL SHOW</div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 font-mono text-[11px]">
                <Cell label="STREAK" value={pulse.streak} accent="amber" />
                <Cell label="INSIGHTS" value={pulse.insights_acted} />
                <Cell label="HEDGES" value={pulse.hedges_accepted} />
                <Cell label="FORECASTS" value={pulse.forecasts_generated} />
                <Cell label="SOURCES" value={pulse.sources_count} />
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-900">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// NEXT BEST ACTION</span>
                <div className="text-sm text-white font-bold mt-1">{pulse.next_best_action.title}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{pulse.next_best_action.body}</div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-zinc-900">
            <ToggleRow
              label="Email me the Activation Pulse every Monday"
              checked={activationOn}
              onChange={v => toggle("activation_pulse_enabled", v)}
              disabled={savingFlag}
              testId="toggle-activation-pulse"
            />
            <button
              onClick={sendMine}
              disabled={sending}
              data-testid="send-my-pulse-btn"
              className="inline-flex items-center gap-2 border border-[#FFB000]/40 hover:bg-[#FFB000]/10 text-[#FFB000] px-4 py-2 font-mono text-[11px] uppercase tracking-widest disabled:opacity-50"
            >
              <Send className={`w-3.5 h-3.5 ${sending ? "animate-pulse" : ""}`} />
              {sending ? "SENDING…" : "EMAIL MINE NOW"}
            </button>
          </div>
        </div>
      </Panel>

      {/* Daily Morning Briefing */}
      <Panel title="// DAILY MORNING BRIEFING · 08:00 UTC" testId="daily-briefing-card">
        <div className="p-5 space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Daily 08:00 UTC email with your top 3 fresh insights for the day. Same workspace branding as your
            other DARA emails. Replies route to DARA Replies (DARA-grounded answers).
          </p>
          <ToggleRow
            label="Send me the Daily Morning Briefing"
            checked={briefingOn}
            onChange={v => toggle("daily_briefing_enabled", v)}
            disabled={savingFlag}
            testId="toggle-daily-briefing"
          />
        </div>
      </Panel>

      {/* DARA Replies */}
      <Panel title="// DARA REPLIES · DARA-GROUNDED" testId="dara-replies-card">
        <div className="p-5 space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Reply to any DARA email with a question (e.g. "explain leg 2 of this hedge"). DARA reads your
            workspace context and answers in &lt; 30 seconds, threaded back to you and your team.
          </p>
          <ToggleRow
            label="Allow DARA to auto-answer email replies"
            checked={repliesOn}
            onChange={v => toggle("email_replies_enabled", v)}
            disabled={savingFlag}
            testId="toggle-dara-replies"
          />
        </div>
      </Panel>
    </div>
  );
}

function Cell({ label, value, accent }) {
  const color = accent === "amber" ? "text-[#FFB000]" : "text-white";
  return (
    <div className="border border-zinc-900 bg-black p-2.5">
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">{label}</div>
      <div className={`font-heading font-black text-xl mt-0.5 ${color}`}>{value ?? "—"}</div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, disabled, testId }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
      <button
        type="button"
        onClick={() => onChange(!checked)}
        disabled={disabled}
        data-testid={testId}
        aria-pressed={checked}
        className={`relative w-10 h-5 transition-colors ${checked ? "bg-[#FFB000]" : "bg-zinc-800"} disabled:opacity-50`}
      >
        <span className={`absolute top-0.5 w-4 h-4 bg-black transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
      </button>
      <span className="text-xs text-zinc-300">{label}</span>
    </label>
  );
}
